/**
 * Translation Hook
 *
 * Simple translation lookup using the current language from useSettings.
 * No external dependencies - just object lookup.
 */

import { useCallback } from 'react';
import { useSettings } from './useSettings';
import translations from '../i18n/translations';

/**
 * Translation hook
 *
 * Usage:
 *   const { t, language, isRTL } = useTranslation();
 *   t('status.loading') => 'Loading...' or 'جاري التحميل...'
 */
export function useTranslation() {
  const { language, isRTL } = useSettings();

  /**
   * Get translation for a key
   * @param {string} key - Translation key (e.g., 'dashboard.title')
   * @param {object} params - Optional parameters for interpolation
   * @returns {string} Translated string or key if not found
   */
  const t = useCallback((key, params = {}) => {
    const translation = translations[key];

    if (!translation) {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    let text = translation[language] || translation.en || key;

    // Simple parameter interpolation: {{param}}
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
    });

    return text;
  }, [language]);

  /**
   * Get all translations for a prefix
   * Useful for getting all date presets, settings, etc.
   * @param {string} prefix - Key prefix (e.g., 'datePresets')
   * @returns {object} Object with shortened keys
   */
  const tGroup = useCallback((prefix) => {
    const result = {};
    const prefixWithDot = prefix + '.';

    Object.keys(translations).forEach(key => {
      if (key.startsWith(prefixWithDot)) {
        const shortKey = key.substring(prefixWithDot.length);
        result[shortKey] = translations[key][language] || translations[key].en;
      }
    });

    return result;
  }, [language]);

  return {
    t,
    tGroup,
    language,
    isRTL
  };
}

export default useTranslation;
